import localforage from 'localforage'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getFirebaseApp } from '@/infra/firebase/app'
import {
  emitter,
  SET_FULLSCREEN_LOADING,
  SIGN_IN_MAIL_SEND,
  SIGN_IN_MAIL_FAIL
} from '@/plugins/emitter'
import {
  getFirebaseAuth,
  createFacebookAuthProvider,
  createAppleAuthProvider,
  createGoogleAuthProvider,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
  signOut as firebaseSignOut,
  fetchSignInMethodsForEmail
} from '@/infra/firebase/auth'
import {
  SIGNUP_RETRY_BLOCKED_ERROR_CODES,
  actionCodeSettings
} from './constants'
import { buildVarunaLoginFormData, postVarunaLogin } from './formDataHelpers'

/** Auth / token / messaging methods bound onto the $firebase Vue host */
export const authMethods = {
  reloadToken(this: any) {
    const app = this
    return this.user
      .getIdToken(true)
      .then(function (idToken: string) {
        app.accessToken = idToken
      })
      .catch(function (err: unknown) {
        console.error(err)
      })
  },

  signInApple(this: any) {
    this.$gtm.hintAuthMethod('apple')
    signInWithRedirect(getFirebaseAuth(), createAppleAuthProvider()).catch(
      (error: unknown) => {
        console.error('signInApple redirect failed', error)
        this.$gtm.clearAuthMethodHint()
        this.showResultMessage(
          'Appleログインを開始できませんでした。時間をおいて再度お試しください。',
          'error'
        )
      }
    )
  },

  signInLine(this: any) {
    this.$gtm.hintAuthMethod('line')
    const LENGTH = 16
    const WORDS = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let rString = ''
    for (let i = 0; i < LENGTH; i++) {
      rString += WORDS[Math.floor(Math.random() * WORDS.length)]
    }
    const clientId = process.env.VUE_APP_LINE_CLIENT_ID
    const encodedCloudFunctionsUrl = encodeURIComponent(
      process.env.VUE_APP_LINE_CALLBACK_URL as string
    )
    location.href =
      'https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=' +
      clientId +
      '&redirect_uri=' +
      encodedCloudFunctionsUrl +
      '/line-login?action=yagish&scope=profile%20openid%20email&state=' +
      rString
  },

  signInFacebook(this: any) {
    this.$gtm.hintAuthMethod('facebook')
    signInWithRedirect(getFirebaseAuth(), createFacebookAuthProvider()).catch(
      (error: unknown) => {
        console.error('signInFacebook redirect failed', error)
        this.$gtm.clearAuthMethodHint()
        this.showResultMessage(
          'Facebookログインを開始できませんでした。時間をおいて再度お試しください。',
          'error'
        )
      }
    )
  },

  signInGoogle(this: any) {
    this.$gtm.hintAuthMethod('google')
    signInWithRedirect(getFirebaseAuth(), createGoogleAuthProvider()).catch(
      (error: unknown) => {
        console.error('signInGoogle redirect failed', error)
        this.$gtm.clearAuthMethodHint()
        this.showResultMessage(
          'Googleログインを開始できませんでした。時間をおいて再度お試しください。',
          'error'
        )
      }
    )
  },

  logInGoogle(this: any) {
    this.$gtm.hintAuthMethod('google')
    signInWithRedirect(getFirebaseAuth(), createGoogleAuthProvider()).catch(
      (error: unknown) => {
        console.error('logInGoogle redirect failed', error)
        this.$gtm.clearAuthMethodHint()
        this.showResultMessage(
          'Googleログインを開始できませんでした。時間をおいて再度お試しください。',
          'error'
        )
      }
    )
  },

  checkEmailExists(this: any, email: string) {
    return fetchSignInMethodsForEmail(getFirebaseAuth(), email).then(
      (methods) => methods && methods.length > 0
    )
  },

  /**
   * options.signInAlreadyTried: 呼び出し側が先にログインを試した上で登録に回している場合。
   * その状況で email-already-in-use が返るのは「登録済みだがパスワードが違う」で確定するため、
   * Firebase の生の英語メッセージではなくログイン失敗として案内する。
   */
  signOnEmailPasswd(this: any, id: string, passwd: string, options?: any) {
    const app = this
    const signInAlreadyTried = !!(options && options.signInAlreadyTried)
    app.$gtm.hintAuthMethod('password')
    return createUserWithEmailAndPassword(getFirebaseAuth(), id, passwd)
      .then((userCredential) => {
        console.error('createUserWithEmailAndPassword then')
        const user = userCredential.user
        app.$message({
          message: '会員登録成功しました。',
          type: 'success'
        })
        const formData = buildVarunaLoginFormData(app, user, {
          includeUnivId: true
        })
        postVarunaLogin(app, formData)
        app.handleDigramExamId(user)
        return true
      })
      .catch((error: any) => {
        console.error(error)
        app.$gtm.clearAuthMethodHint()
        if (signInAlreadyTried && error.code === 'auth/email-already-in-use') {
          app.$message({
            message:
              'ログインに失敗しました。メールアドレスまたはパスワードが違います',
            type: 'error'
          })
          return false
        }
        const errorMessage = error.message
        console.error(errorMessage)
        app.$message({
          message: '登録に失敗しました。' + errorMessage,
          type: 'error'
        })
        return false
      })
  },

  /**
   * options.signupFallback: 「まずログインを試し、失敗したら新規登録に回す」呼び出し側向け。
   * 戻り値: true=ログイン成功 / 'retry-as-signup'=新規登録として再試行 / false=失敗（表示済み）
   */
  signInEmailPasswd(this: any, id: string, passwd: string, options?: any) {
    console.error('signInEmailPasswd')
    const app = this
    const signupFallback = !!(options && options.signupFallback)
    app.$gtm.hintAuthMethod('password')
    return signInWithEmailAndPassword(getFirebaseAuth(), id, passwd)
      .then((userCredential) => {
        const user = userCredential.user
        console.error(user)
        app.$message({
          message: 'ログインに成功しました。',
          type: 'success'
        })
        console.error(9999)
        app.isLoginAction = true
        const formData = buildVarunaLoginFormData(app, user)
        postVarunaLogin(app, formData)
        app.handleDigramExamId(user)
        return true
      })
      .catch((error: any) => {
        app.$gtm.clearAuthMethodHint()
        if (
          signupFallback &&
          SIGNUP_RETRY_BLOCKED_ERROR_CODES.indexOf(error.code) === -1
        ) {
          return 'retry-as-signup'
        }
        const errorCode = error.code
        let errorMessage: string | null = ''

        switch (errorCode) {
          case 'auth/cancelled-popup-request':
          case 'auth/popup-closed-by-user':
            errorMessage = null
            break
          case 'auth/email-already-in-use':
            errorMessage = 'このメールアドレスは使用されています'
            break
          case 'auth/invalid-email':
            errorMessage = 'メールアドレスの形式が正しくありません'
            break
          case 'auth/user-disabled':
            errorMessage = 'サービスの利用が停止されています'
            break
          case 'auth/user-not-found':
            errorMessage = 'メールアドレスまたはパスワードが違います'
            break
          case 'auth/user-mismatch':
            errorMessage = 'メールアドレスまたはパスワードが違います'
            break
          case 'auth/weak-password':
            errorMessage = 'パスワードは6文字以上にしてください'
            break
          case 'auth/wrong-password':
            errorMessage = 'メールアドレスまたはパスワードが違います'
            break
          case 'auth/popup-blocked':
            errorMessage =
              '認証ポップアップがブロックされました。ポップアップブロックをご利用の場合は設定を解除してください'
            break
          case 'auth/operation-not-supported-in-this-environment':
          case 'auth/auth-domain-config-required':
          case 'auth/operation-not-allowed':
          case 'auth/unauthorized-domain':
            errorMessage = '現在この認証方法はご利用頂けません'
            break
          case 'auth/requires-recent-login':
            errorMessage = '認証の有効期限が切れています'
            break
          default:
            errorMessage =
              'エラーが発生しました。しばらく時間をおいてお試しください'
            break
        }
        emitter.emit(SET_FULLSCREEN_LOADING, false)
        app.$message({
          message: 'ログインに失敗しました。' + errorMessage,
          type: 'error'
        })
        return false
      })
  },

  signInEmail(this: any, email: string) {
    console.error('signInEmail')
    const app = this
    sendSignInLinkToEmail(getFirebaseAuth(), email, actionCodeSettings)
      .then(function () {
        app.$message({
          message: email + 'にログインURLを送りました',
          type: 'success'
        })
        emitter.emit(SIGN_IN_MAIL_SEND)
        localforage.setItem('emailForSignIn', email)
      })
      .catch(function (error: unknown) {
        console.error(error)
        app.$message({
          message: 'メールログインが失敗しました',
          type: 'error'
        })
        emitter.emit(SIGN_IN_MAIL_FAIL)
      })
  },

  signOut(this: any, options?: { skipMessage?: boolean }) {
    const app = this
    const skipMessage = !!(options && options.skipMessage)
    firebaseSignOut(getFirebaseAuth())
      .then(function () {
        app.$cookies.config(0, '')
        app.$cookies.remove('_YAGISH_SESSION_KEY', null, 'yagish.jp')
        app.$cookies.set(
          '_YAGISH_SESSION_LOGOUT',
          'LOGOUT',
          null,
          null,
          'yagish.jp'
        )
        if (!skipMessage) {
          app.$message({
            message: 'ログアウトしました',
            type: 'info'
          })
        }
        location.href = '/login'
      })
      .catch(function (error: unknown) {
        app.$message({
          message: 'ログアウトに失敗しました',
          type: 'error'
        })
        console.error(error)
      })
  },

  deleteUser(this: any) {
    if (!this.isAuthenticated || !this.user) {
      this.$message({
        message: 'アカウントの削除にはログインが必要です',
        type: 'error'
      })
      return false
    }
    return this.user.delete()
  },

  showResultMessage(this: any, message: string, type?: string) {
    this.$message({
      message,
      type,
      duration: 6000
    })
  },

  authTest(this: any) {
    const auth = httpsCallable(getFunctions(getFirebaseApp()), 'authTest')
    auth({ text: 'test' })
      .then(function (result) {
        console.log(result)
      })
      .catch((err) => {
        console.error(err)
      })
  },

  handleDigramExamId(this: any, user: any) {
    const digramExamId = this.$cookies.get('digram_exam_id')
    this.$cookies.remove('digram_exam_id')
    if (digramExamId && user && user.uid) {
      const formData = new FormData()
      formData.append('random_id', digramExamId)
      formData.append('uuid', user.uid)
      this.$axios
        .post(this.$varuna.endPoints.loginDigram, formData)
        .then(function (response: unknown) {
          console.log(response)
        })
        .catch(function (error: unknown) {
          console.error(error)
        })
    }
  }
}
